import React from 'react';
import classnames from 'classnames';

import { Plan } from '@api/types';
import { Feature } from '@react/Pages/AccountSettingsPage/PricingPlanPage/features';
import {
  allowChangePlan,
  allowChoosePlan,
  isCompatibleWithYearly,
  shouldShowAttributions,
} from '@redux/ducks/account/pricingPlan/pricingPlans.utils';
import {
  getAttributions,
  getAttributionPrice,
  getYearlySaving,
  getBasePrice,
  getAttributionSaving,
} from './formatters';
import { PricingPlanActionButton } from './PricingPlanActionButton';

import './PricingPlan.scss';

export interface PricingPlanProps {
  onConfirm: (plan: Plan) => void;
  onCancel: (plan: Plan) => void;
  plan: Plan;
  currentPlan?: Plan;
  isYearly: boolean;
  monthlyPaymentPlans: Plan[];
  features: Feature[];
}

const LabelFree: React.FC = () => (
  <div className="flex-box-column flex-justify-center pricing-plan__feature free">{t('label.free')}</div>
);

export const PricingPlan: React.FC<PricingPlanProps> = ({
  onConfirm,
  onCancel,
  plan,
  currentPlan,
  isYearly,
  monthlyPaymentPlans,
  features,
}) => {
  const attributionPackageClassNames = classnames('flex-one', 'pricing-plan', {
    'pricing-plan--active': plan.current,
    'pricing-plan--compatible-with-yearly': isCompatibleWithYearly(plan, currentPlan),
  });

  const attributionClassNames = classnames('flex-box-column', 'flex-justify-center', 'pricing-plan__attribution', {
    'pricing-plan__attribution--yearly': isYearly,
  });

  const actionClassNames = classnames('pricing-plan__action', {
    'pricing-plan__action--scheduled': plan.scheduled,
  });

  const isChangeAllowed = allowChangePlan(plan, currentPlan);
  const isChooseAllowed = allowChoosePlan(plan, currentPlan);
  const showBasePrice = isChooseAllowed || isChangeAllowed || plan.current;

  return (
    <div className={attributionPackageClassNames}>
      <h2 className="pricing-plan__title">{plan.name}</h2>

      <div className="pricing-plan__features-list">
        <LabelFree />
        <LabelFree />
        <LabelFree />
        <div className="flex-box-column flex-justify-center pricing-plan__feature free">
          {shouldShowAttributions(plan) ? (
            <span>{getAttributions(plan)}</span>
          ) : (
            <span>{t('pricing-plan.change-package.individual')}</span>
          )}
        </div>
        <div className={attributionClassNames}>
          {shouldShowAttributions(plan) ? (
            <>
              <span>{getAttributionPrice(plan.attributionPrice, plan.currency)}</span>
              {isYearly && !!plan.attributionPrice && (
                <span className="pricing-plan__saving">
                  {getYearlySaving(plan.currency, plan.attributionPrice, plan.name, monthlyPaymentPlans)}
                </span>
              )}
            </>
          ) : (
            <span>{t('pricing-plan.change-package.individual')}</span>
          )}
        </div>

        {features.map(({ key }) => (
          <div className="flex-box-column flex-justify-center pricing-plan__feature" key={key}>
            {plan.features[key].enabled && (
              <div>
                <i className="icon-checkmark pricing-plan__check-mark" />
              </div>
            )}
          </div>
        ))}

        <div
          className={classnames('flex-box-column', 'flex-justify-center', {
            'pricing-plan__base-price': showBasePrice,
            'pricing-plan__contact-us-text': !showBasePrice,
          })}
        >
          {showBasePrice ? (
            <span>{getBasePrice(plan.currency, plan.basePrice)}</span>
          ) : (
            <span className="get-in-touch-text">{t('pricing-plan.change-package.help.description-short')}</span>
          )}
        </div>

        <div className={actionClassNames}>
          <PricingPlanActionButton currentPlan={currentPlan} plan={plan} onCancel={onCancel} onConfirm={onConfirm} />

          {isYearly && (
            <span className="pricing-plan__attribution-increase">
              {getAttributionSaving(plan, monthlyPaymentPlans)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
